import { useEffect, useState, useCallback, useMemo } from 'react';
import useSessionStorage from './useSessionStorage';

export type PropertyIdSource = 'url' | 'session' | 'manual' | 'none';

export interface PropertyIdResolver {
  propertyId: string | null;
  source: PropertyIdSource;
  isLoading: boolean;
}

export interface PropertyIdResolution extends PropertyIdResolver {
  setPropertyId: (propertyId: string | null) => void;
}

export function usePropertyIdResolver(): PropertyIdResolution {
  // Get session property ID first (hooks must go first!)
  const { storedValue: sessionPropertyId, setValue: setSessionPropertyId } = useSessionStorage(
    'decisionMakingLastActiveProperty',
    null
  );

  // Extract property ID from URL once
  const urlPropertyId = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('propertyId');
  }, []);

  // Determine initial value synchronously
  let initialPropertyId: string | null = null;
  let initialSource: PropertyIdSource = 'none';

  if (urlPropertyId) {
    initialPropertyId = urlPropertyId;
    initialSource = 'url';
  } else if (sessionPropertyId) {
    initialPropertyId = sessionPropertyId;
    initialSource = 'session';
  }

  const [ resolution, setResolution ] = useState<PropertyIdResolver>({
    propertyId: initialPropertyId,
    source: initialSource,
    isLoading: false,
  });

  // Update session storage on initial URL param (but avoid doing this synchronously outside effect)
  useEffect(() => {
    if (urlPropertyId) {
      setSessionPropertyId(urlPropertyId);
    }
  }, [ urlPropertyId, setSessionPropertyId ]);

  // Allow manual override
  const setPropertyId = useCallback(
    (newPropertyId: string | null) => {
      setSessionPropertyId(newPropertyId);

      setResolution({
        propertyId: newPropertyId,
        source: newPropertyId ? 'manual' : 'none',
        isLoading: false,
      });
    },
    [ setSessionPropertyId ]
  );

  // Listen for session changes (e.g., across tabs) if no current propertyId
  useEffect(() => {
    if (!resolution.propertyId && sessionPropertyId) {
      setResolution({
        propertyId: sessionPropertyId,
        source: 'session',
        isLoading: false,
      });
    }
  }, [ sessionPropertyId, resolution.propertyId ]);

  return {
    ...resolution,
    setPropertyId,
  };
}
